import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'
import PropTypes from 'prop-types'

const ActionCableContext = createContext(null)

export const ActionCableProvider = ({ cable, url, children }) => {
  const consumer = useMemo(() => cable || createConsumer(url), [cable, url])

  useEffect(() => {
    if (cable) return undefined
    return () => consumer.disconnect()
  }, [cable, consumer])

  return (
    <ActionCableContext.Provider value={consumer}>
      {children}
    </ActionCableContext.Provider>
  )
}

ActionCableProvider.displayName = 'ActionCableProvider'

ActionCableProvider.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  cable: PropTypes.object,
  url: PropTypes.string,
  children: PropTypes.node,
}

export const ActionCable = ({
  channel,
  onReceived,
  onInitialized,
  onConnected,
  onDisconnected,
  onRejected,
}) => {
  const consumer = useContext(ActionCableContext)

  // Keep callbacks in refs so the subscription survives parent re-renders
  // without resubscribing on callback identity changes.
  const handlers = useRef({})
  handlers.current = {
    onReceived,
    onInitialized,
    onConnected,
    onDisconnected,
    onRejected,
  }

  const channelKey = useMemo(
    () => (typeof channel === 'string' ? channel : JSON.stringify(channel)),
    [channel],
  )

  useEffect(() => {
    if (!consumer) {
      throw new Error(
        'ActionCable must be rendered inside an ActionCableProvider',
      )
    }

    const subscription = consumer.subscriptions.create(channel, {
      received(data) {
        if (handlers.current.onReceived) handlers.current.onReceived(data)
      },
      initialized() {
        if (handlers.current.onInitialized) handlers.current.onInitialized()
      },
      connected() {
        if (handlers.current.onConnected) handlers.current.onConnected()
      },
      disconnected() {
        if (handlers.current.onDisconnected) handlers.current.onDisconnected()
      },
      rejected() {
        if (handlers.current.onRejected) handlers.current.onRejected()
      },
    })

    return () => consumer.subscriptions.remove(subscription)
  }, [consumer, channelKey])

  return null
}

ActionCable.displayName = 'ActionCable'

ActionCable.propTypes = {
  channel: PropTypes.oneOfType([
    PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    PropTypes.object,
  ]).isRequired,
  onReceived: PropTypes.func,
  onInitialized: PropTypes.func,
  onConnected: PropTypes.func,
  onDisconnected: PropTypes.func,
  onRejected: PropTypes.func,
}
